import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Timeline } from "@/components/Timeline";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Sparkles, Check, Save, CreditCard, Wand2, Clapperboard } from "lucide-react";
import { PaymentBadges } from "@/components/PaymentBadges";

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [screenplay, setScreenplay] = useState("");
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payPhase, setPayPhase] = useState("deposit");
  const [buyer, setBuyer] = useState({ name: "", surname: "", address: "", city: "İstanbul", gsmNumber: "+905555555555", identityNumber: "11111111111" });

  const load = async () => {
    const r = await api.get(`/projects/${id}`);
    setProject(r.data);
    setScreenplay(r.data.screenplay || "");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (project?.ai_status !== "generating") return;
    const t = setInterval(async () => {
      const r = await api.get(`/projects/${id}`);
      setProject(r.data);
      if (r.data.ai_status !== "generating") {
        setScreenplay(r.data.screenplay || "");
        if (r.data.ai_status === "error") toast.error("AI senaryo üretilemedi, tekrar deneyin.");
        else toast.success("2 senaryo alternatifin hazır!");
      }
    }, 3000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [project?.ai_status, id]);

  if (!project) return <div className="max-w-4xl mx-auto px-6 py-12 text-white/40 animate-pulse">Yükleniyor…</div>;

  const generating = project.ai_status === "generating";
  const options = project.scenario_options || [];
  const editable = ["scenario", "approved"].includes(project.status);
  const payAmount = payPhase === "final" ? project.final_amount : project.deposit;

  const generate = async () => {
    try {
      await api.post(`/projects/${id}/generate-scenario`);
      setProject({ ...project, ai_status: "generating" });
      toast.info("AI iki senaryo alternatifi yazıyor, bu 1-2 dakika sürebilir…");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const selectScenario = async (index) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/select-scenario`, { index });
      setProject(r.data);
      setScreenplay(r.data.screenplay);
      toast.success("Senaryo seçildi, şimdi düzenleyebilirsin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const saveScreenplay = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/projects/${id}/screenplay`, { screenplay });
      setProject(r.data);
      toast.success("Senaryo kaydedildi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/approve`);
      setProject(r.data);
      toast.success("Senaryo onaylandı, stüdyo teklif hazırlayacak");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const openPay = (phase) => { setPayPhase(phase); setPayOpen(true); };

  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/projects/${id}/payment/initialize`, { ...buyer, phase: payPhase });
      window.location.assign(r.data.paymentPageUrl);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <button onClick={() => navigate("/dashboard")} data-testid="back-button"
        className="flex items-center gap-2 text-white/50 hover:text-gold transition-colors mb-8 text-sm">
        <ArrowLeft className="w-4 h-4" /> Panele dön
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-4xl lg:text-5xl mb-2">{project.title}</h1>
          <StatusBadge status={project.status} testid="detail-status-badge" />
        </div>
        {project.price_total && (
          <div className="text-right">
            <p className="text-xs uppercase tracking-widest text-white/40">Toplam Bütçe</p>
            <p className="font-serif text-3xl text-gold">{project.price_total.toLocaleString("tr-TR")} ₺</p>
            <p className="text-sm text-white/50">%50 Ön Ödeme: {project.deposit?.toLocaleString("tr-TR")} ₺</p>
          </div>
        )}
      </div>

      <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 mb-8">
        <Timeline status={project.status} />
      </div>

      <section className="mb-8">
        <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Fikrin</p>
        <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 text-white/80 whitespace-pre-wrap leading-relaxed">
          {project.idea}
        </div>
      </section>

      {/* Screenplay */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-widest text-white/40">AI Senaryo</p>
          {project.status === "idea" && !generating && (
            <Button onClick={generate} data-testid="generate-scenario-button"
              className="bg-gold text-black hover:bg-gold-light rounded-md">
              <Sparkles className="w-4 h-4 mr-2" /> AI Senaryo Üret
            </Button>
          )}
        </div>

        {generating ? (
          <div className="bg-[#0A0A0A] border border-gold/20 rounded-xl p-12 text-center" data-testid="ai-generating">
            <Sparkles className="w-8 h-8 text-gold mx-auto mb-4 animate-pulse" />
            <p className="font-serif text-2xl mb-2">AI senaryo alternatiflerini yazıyor…</p>
            <p className="text-white/50 text-sm">İki farklı sinematik yaklaşım hazırlanıyor, bu 1-2 dakika sürebilir.</p>
          </div>
        ) : options.length > 0 && !project.screenplay ? (
          <div data-testid="scenario-options">
            <p className="text-white/60 text-sm mb-4">İki alternatif hazır. Devam etmek istediğin senaryoyu seç, sonra istediğin gibi düzenle.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {options.map((opt, i) => (
                <div key={i} className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 flex flex-col hover:border-gold/30 transition-colors" data-testid={`scenario-option-${i}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 rounded-full bg-gold/15 text-gold text-xs flex items-center justify-center">{i + 1}</span>
                    <span className="text-xs uppercase tracking-widest text-gold">
                      {i === 0 ? "Duygusal · Karakter Odaklı" : "Tempolu · Görsel"}
                    </span>
                  </div>
                  <div className="bg-[#121212] border border-white/5 rounded-lg p-4 text-white/70 text-sm font-mono leading-relaxed max-h-72 overflow-y-auto whitespace-pre-wrap mb-4 flex-1">
                    {opt}
                  </div>
                  <Button onClick={() => selectScenario(i)} disabled={busy} data-testid={`select-scenario-${i}`}
                    className="bg-gold text-black hover:bg-gold-light rounded-md w-full">
                    <Check className="w-4 h-4 mr-2" /> Bu Senaryoyu Seç
                  </Button>
                </div>
              ))}
            </div>
            <Button onClick={generate} variant="outline" data-testid="regenerate-options-button"
              className="mt-5 border-white/20 bg-transparent hover:bg-white/5 rounded-md">
              <Wand2 className="w-4 h-4 mr-2" /> İki Alternatifi Yeniden Üret
            </Button>
          </div>
        ) : project.screenplay ? (
          <>
            <Textarea value={screenplay} onChange={(e) => setScreenplay(e.target.value)} readOnly={!editable} rows={20}
              data-testid="screenplay-editor"
              className="bg-[#121212] border-white/10 focus:border-gold focus-visible:ring-gold font-mono text-sm leading-relaxed resize-y" />
            {editable && (
              <div className="flex flex-wrap gap-3 mt-4">
                <Button onClick={saveScreenplay} disabled={busy} variant="outline" data-testid="save-screenplay-button"
                  className="border-white/20 bg-transparent hover:bg-white/5 rounded-md">
                  <Save className="w-4 h-4 mr-2" /> Kaydet
                </Button>
                <Button onClick={generate} disabled={busy} variant="outline" data-testid="regenerate-button"
                  className="border-white/20 bg-transparent hover:bg-white/5 rounded-md">
                  <Wand2 className="w-4 h-4 mr-2" /> Yeni Alternatifler Üret
                </Button>
                {project.status === "scenario" && (
                  <Button onClick={approve} disabled={busy} data-testid="approve-scenario-button"
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-md">
                    <Check className="w-4 h-4 mr-2" /> Senaryoyu Onayla
                  </Button>
                )}
              </div>
            )}
            {project.status === "approved" && (
              <p className="text-sm text-emerald-400 mt-4">✓ Senaryo onaylandı. Stüdyo maliyet teklifini hazırlıyor.</p>
            )}
          </>
        ) : (
          <div className="bg-[#0A0A0A] border border-dashed border-white/10 rounded-xl p-10 text-center text-white/40">
            Henüz senaryo üretilmedi. Yukarıdaki butonla AI senaryonu oluştur.
          </div>
        )}
      </section>

      {/* Quote / Deposit payment */}
      {project.status === "quoted" && (
        <section className="bg-[#0A0A0A] border border-gold/30 rounded-xl p-7 mb-8" data-testid="quote-section">
          <p className="text-xs uppercase tracking-widest text-gold mb-2">Teklif Hazır</p>
          <h2 className="font-serif text-3xl mb-3">Prodüksiyon Teklifiniz</h2>
          {project.quote_note && <p className="text-white/70 mb-5 whitespace-pre-wrap">{project.quote_note}</p>}
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-white/50 text-sm">Toplam {project.price_total?.toLocaleString("tr-TR")} ₺ • Şimdi ödenecek (%50)</p>
              <p className="font-serif text-4xl text-gold">{project.deposit?.toLocaleString("tr-TR")} ₺</p>
            </div>
            <Button onClick={() => openPay("deposit")} data-testid="accept-quote-button"
              className="bg-gold text-black hover:bg-gold-light rounded-md h-12 px-8">
              <CreditCard className="w-4 h-4 mr-2" /> Teklifi Kabul Et & Öde
            </Button>
          </div>
        </section>
      )}

      {project.status === "paid" && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6 mb-8 text-emerald-300" data-testid="paid-banner">
          ✓ Ön ödemeniz alındı. Projeniz kısa süre içinde üretime alınacak.
        </div>
      )}
      {project.status === "in_production" && (
        <div className="bg-gold/10 border border-gold/30 rounded-xl p-6 mb-8 text-gold flex items-center gap-3" data-testid="production-banner">
          <Clapperboard className="w-5 h-5" /> Projeniz üretimde. Ekibimiz çalışıyor, teslimat için sizi bilgilendireceğiz.
        </div>
      )}

      {/* Delivered → final payment */}
      {project.status === "delivered" && (
        <section className="bg-[#0A0A0A] border border-gold/30 rounded-xl p-7 mb-8" data-testid="final-payment-section">
          <p className="text-xs uppercase tracking-widest text-gold mb-2">Teslim Edildi — Son Adım</p>
          <h2 className="font-serif text-3xl mb-3">Projeniz teslim edildi 🎬</h2>
          {project.delivery_note && <p className="text-white/70 mb-3 whitespace-pre-wrap">{project.delivery_note}</p>}
          {project.delivery_url && (
            <a href={project.delivery_url} target="_blank" rel="noreferrer" className="text-gold hover:underline mb-4 inline-block">
              Teslimat bağlantısını aç →
            </a>
          )}
          <div className="flex flex-wrap items-end justify-between gap-4 mt-4 pt-4 border-t border-white/10">
            <div>
              <p className="text-white/50 text-sm">Projeyi tamamlamak için kalan %50 ödeme</p>
              <p className="font-serif text-4xl text-gold">{project.final_amount?.toLocaleString("tr-TR")} ₺</p>
            </div>
            <Button onClick={() => openPay("final")} data-testid="pay-final-button"
              className="bg-gold text-black hover:bg-gold-light rounded-md h-12 px-8">
              <CreditCard className="w-4 h-4 mr-2" /> Kalan Ödemeyi Yap
            </Button>
          </div>
        </section>
      )}

      {project.status === "completed" && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-6 mb-8" data-testid="completed-banner">
          <p className="text-emerald-300 font-medium mb-2">✓ Projeniz tamamlandı!</p>
          {project.delivery_note && <p className="text-white/70 whitespace-pre-wrap mb-2">{project.delivery_note}</p>}
          {project.delivery_url && (
            <a href={project.delivery_url} target="_blank" rel="noreferrer" className="text-gold hover:underline block mb-2">
              Teslimat bağlantısını aç →
            </a>
          )}
          <p className="text-white/50 text-sm">Faturanız e-posta adresinize PDF olarak gönderildi.</p>
        </div>
      )}

      {/* Payment dialog (deposit + final) */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Fatura Bilgileri</DialogTitle>
            <DialogDescription className="text-white/50">
              {payPhase === "final" ? "Kalan %50" : "%50 ön"} ödemeniz için fatura bilgilerinizi girin. Güvenli ödeme sayfasına yönlendirileceksiniz.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={pay} className="space-y-4" data-testid="payment-form">
            <PaymentBadges className="justify-center pb-1" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Ad</Label>
                <Input required value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })}
                  data-testid="buyer-name-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold" />
              </div>
              <div>
                <Label className="text-white/70">Soyad</Label>
                <Input required value={buyer.surname} onChange={(e) => setBuyer({ ...buyer, surname: e.target.value })}
                  data-testid="buyer-surname-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold" />
              </div>
            </div>
            <div>
              <Label className="text-white/70">Adres</Label>
              <Input required value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })}
                data-testid="buyer-address-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/70">Şehir</Label>
                <Input required value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })}
                  data-testid="buyer-city-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold" />
              </div>
              <div>
                <Label className="text-white/70">Telefon</Label>
                <Input value={buyer.gsmNumber} onChange={(e) => setBuyer({ ...buyer, gsmNumber: e.target.value })}
                  data-testid="buyer-phone-input" className="mt-1 bg-[#121212] border-white/10 focus:border-gold" />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="pay-now-button"
                className="bg-gold text-black hover:bg-gold-light rounded-md w-full h-11">
                {busy ? "Yönlendiriliyor…" : `${payAmount?.toLocaleString("tr-TR")} ₺ Öde`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
